import { getEmployeesWhoPerformServices } from '@/helpers/placeHelper';
import { serviceIsExtraTimeAddOn } from '@/helpers/service';
import { Place, PlaceEmployee, PlaceService } from '@/types/state/shared';
import { resolveEmployeeFromUrl } from './resolveEmployeeFromUrl';
import { resolveMainServicesFromUrl } from './resolveMainServicesFromUrl';
import { parseSelection } from './urlState';

/**
 * What the calendar does with the employee: an absent `sr` says nothing, so the store's
 * employee stands; the sentinel, an unknown externalId or an employee who cannot take the
 * whole selection all read as any employee.
 */
export type CalendarUrlEmployee = { kind: 'keep' } | { kind: 'any' } | { kind: 'pick'; employee: PlaceEmployee };

export type CalendarUrlSelection = {
  mainServices: PlaceService[];
  addOns: PlaceService[];
  employee: CalendarUrlEmployee;
  /** What the link named that the place no longer offers, for the arrival notice. */
  dropped: {
    serviceIds: number[];
    /** `missing` — no employee carries the externalId; `cannotPerform` — one does, but not for every named service. */
    employee?: 'missing' | 'cannotPerform';
  };
};

const parseIds = (ids: string | undefined): number[] =>
  (ids?.split(',') ?? []).map((id) => parseInt(id, 10)).filter((id) => !Number.isNaN(id));

/**
 * The calendar's reader for `ss`/`rs`/`sr`, for links that arrive with a whole selection.
 * Returns undefined when the URL carries no `ss`: that is a legacy entry (place page, "book
 * again", the `/share` redirect, the widget's `?sr=` start) whose selection the bootstrap
 * already put in the store, and which this reader must leave alone.
 *
 * Add-ons resolve through the picked mains' own `extra.addOnServices`, never through the
 * flattened `place.services` categories — the importer marks add-ons inactive and the
 * catalogue filter drops them, so only a parent service sees its add-ons at all. An add-on
 * may belong to any named main, not only the last one as the add-on step assumes: a link
 * can name two services and both their add-ons, which the step-by-step flow cannot.
 *
 * The employee has to perform every named service. Availability is fetched for one
 * employee across the whole booking, so a partial match renders an empty calendar with no
 * explanation; dropping the employee keeps the booking the visitor was promised.
 *
 * `fallbackMainServices` is the floor: when nothing in `ss` resolves, the selection is what the
 * bootstrap already picked from the path — `place.services` can be narrowed (last-minute, deal and
 * bookability filters) where the store's own main came from `/service/:id`, so `rs` still resolves
 * through that main and only an id the selection does not end up holding is reported dropped.
 */
export function resolveCalendarSelectionFromUrl(
  search: string,
  place: Place,
  fallbackMainServices: PlaceService[],
): CalendarUrlSelection | undefined {
  const selection = parseSelection(search);
  if (!selection.ss) return undefined;

  const ssIds = parseIds(selection.ss);
  const resolvedMains = resolveMainServicesFromUrl(selection.ss, place);
  const mainServices = resolvedMains.length > 0 ? resolvedMains : fallbackMainServices;

  const rsIds = parseIds(selection.rs);
  const addOnCandidates = mainServices.flatMap((main) => (main.extra?.addOnServices ?? []) as PlaceService[]);
  const addOns = addOnCandidates.filter(
    (addOn, index) =>
      addOn.id != null &&
      rsIds.includes(addOn.id) &&
      addOn.about?.settings?.isAddOn &&
      !serviceIsExtraTimeAddOn(addOn) &&
      addOnCandidates.findIndex((candidate) => candidate.id === addOn.id) === index,
  );

  // Measured against the selection the calendar ends up with, not against `ss` alone: on the
  // fallback the main is the bootstrap's own, and an id the visitor can plainly see in the summary
  // must not be announced as no longer offered. Deduplicated because the count is what the notice
  // renders, and a link is free to name an id twice.
  const resolvedIds = [...mainServices, ...addOns].map((service) => service.id);
  const namedIds = [...ssIds, ...rsIds].filter((id, index, ids) => ids.indexOf(id) === index);
  const serviceIds = namedIds.filter((id) => !resolvedIds.includes(id));

  const { employee, droppedEmployee } = resolveEmployee(selection.sr, place, [...mainServices, ...addOns]);

  return {
    mainServices,
    addOns,
    employee,
    dropped: droppedEmployee ? { serviceIds, employee: droppedEmployee } : { serviceIds },
  };
}

function resolveEmployee(
  sr: string | undefined,
  place: Place,
  services: PlaceService[],
): { employee: CalendarUrlEmployee; droppedEmployee?: 'missing' | 'cannotPerform' } {
  if (!sr) return { employee: { kind: 'keep' } };

  const bookable: PlaceEmployee[] = getEmployeesWhoPerformServices(
    place.employees ?? [],
    services.map((service) => service.id),
  );
  const { matched, isAnyEmployee } = resolveEmployeeFromUrl(sr, bookable);
  if (matched) return { employee: { kind: 'pick', employee: matched } };
  if (isAnyEmployee) return { employee: { kind: 'any' } };

  const named = resolveEmployeeFromUrl(sr, place.employees).matched;
  return { employee: { kind: 'any' }, droppedEmployee: named ? 'cannotPerform' : 'missing' };
}
